import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ProfileWizardProvider } from "@/contexts/ProfileWizardContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/providers/AuthProvider";
import { ZendeskMessengerAuthSync } from "@/components/support/ZendeskMessengerAuthSync";
import { CookieConsentBanner } from "@/components/cookies/CookieConsentBanner";
import { MarketingLayout } from "@/components/layouts";
import { env } from "@/config/env";
import { captureAttributionFromSearchParams } from "@/lib/analytics/properties";
import { getMarketingBaseUrl } from "@/lib/utils";

// REK-1986 (SSO.5, D-9): blocking email-capture modal for phone-only Clerk
// users. Mounted at the app root (next to the wizard providers) so it covers
// every authenticated route; lazy so @clerk/clerk-react stays out of the
// bundle when VITE_AUTH_PROVIDER=supabase.
const ClerkEmailCaptureModal = lazy(
  () => import("@/components/auth/ClerkEmailCaptureModal"),
);

// Critical paths - keep eager
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

/** Client hop to Next marketing. Same-origin is a no-op so local dev does not reload forever. */
function MarketingNextRedirect({ path }: { path: string }) {
  useEffect(() => {
    captureAttributionFromSearchParams(new URLSearchParams(window.location.search));
    const url = new URL(path, getMarketingBaseUrl());
    if (window.location.search) {
      const incoming = new URLSearchParams(window.location.search);
      incoming.forEach((value, key) => {
        if (!url.searchParams.has(key)) {
          url.searchParams.set(key, value);
        }
      });
    }
    if (window.location.hash && !url.hash) {
      url.hash = window.location.hash;
    }
    if (url.origin === window.location.origin) return;
    window.location.replace(url.toString());
  }, [path]);
  return null;
}

// Lazy load all pages for better initial load performance
const Investors = lazy(() => import("./pages/Investors"));
const PressRelease = lazy(() => import("./pages/PressRelease"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Predictions = lazy(() => import("./pages/Predictions"));
const CampaignLanding = lazy(() => import("./pages/CampaignLanding"));
const SurveyDeepLink = lazy(() => import("./pages/SurveyDeepLink"));
const AuthConfirm = lazy(() => import("./pages/AuthConfirm"));
const AuthOneTap = lazy(() => import("./pages/AuthOneTap"));
const AuthSignup = lazy(() => import("./pages/AuthSignup"));
// REK-2137: Google OAuth redirect callback for the login-box pivot.
const AuthSsoCallback = lazy(() => import("./pages/AuthSsoCallback"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const DocuSignCallback = lazy(() => import("./pages/DocuSignCallback"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const B2B = lazy(() => import("./pages/B2B"));
const Webinars = lazy(() => import("./pages/Webinars"));
const WebinarDetail = lazy(() => import("./pages/WebinarDetail"));
const Partners = lazy(() => import("./pages/Partners"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const ContactSales = lazy(() => import("./pages/ContactSales"));
const Careers = lazy(() => import("./pages/Careers"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const PartnerBlog = lazy(() => import("./pages/PartnerBlog"));
const PartnerBlogArticle = lazy(() => import("./pages/PartnerBlogArticle"));

// Legal pages
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const DoNotSellMyData = lazy(() => import("./pages/legal/DoNotSellMyData"));
const NoticeOfIncentive = lazy(() => import("./pages/legal/NoticeOfIncentive"));
const DataBrokerPrivacyNotice = lazy(
  () => import("./pages/legal/DataBrokerPrivacyNotice"),
);
const DeleteMyAccount = lazy(() => import("./pages/legal/DeleteMyAccount"));
const Optout = lazy(() => import("./pages/Optout"));
const DownloadApp = lazy(() => import("./pages/DownloadApp"));
const ReklaimInvest = lazy(() => import("./pages/ReklaimInvest"));
const Signup = lazy(() => import("./pages/Signup"));
const GeoBlocked = lazy(() => import("./pages/GeoBlocked"));

// Optimized QueryClient with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute - reduce refetches
      gcTime: 1000 * 60 * 5, // 5 minutes garbage collection
      retry: 1, // Single retry on failure
      refetchOnWindowFocus: false, // Don't refetch on tab focus
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-muted-foreground">Loading...</span>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <ZendeskMessengerAuthSync />
            {env.authProvider === "clerk" && (
              <Suspense fallback={null}>
                <ClerkEmailCaptureModal />
              </Suspense>
            )}
            <ProfileWizardProvider>
              <ErrorBoundary>
                <Toaster />
                <Sonner />
                <ScrollToTop />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Marketing pages -- shared Navbar + Footer via MarketingLayout */}
                    <Route element={<MarketingLayout />}>
                      <Route
                        path="/"
                        element={<MarketingNextRedirect path="/" />}
                      />
                      <Route
                        path="/how-it-works"
                        element={<MarketingNextRedirect path="/how-it-works" />}
                      />
                      <Route
                        path="/earn"
                        element={
                          <MarketingNextRedirect path="/how-it-works#earn" />
                        }
                      />
                      <Route path="/b2b" element={<B2B />} />
                      <Route path="/investors" element={<Investors />} />
                      <Route
                        path="/investors/press/:slug"
                        element={<PressRelease />}
                      />
                      <Route path="/webinars" element={<Webinars />} />
                      <Route
                        path="/webinars/:slug"
                        element={<WebinarDetail />}
                      />
                      <Route path="/partners" element={<Partners />} />
                      <Route path="/contact-us" element={<ContactUs />} />
                      <Route path="/contact-sales" element={<ContactSales />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route path="/learn" element={<Blog />} />
                      <Route path="/learn/:slug" element={<BlogArticle />} />
                      <Route path="/partner-blog" element={<PartnerBlog />} />
                      <Route
                        path="/partner-blog/:slug"
                        element={<PartnerBlogArticle />}
                      />
                      <Route path="/predictions" element={<Predictions />} />
                      <Route path="/help" element={<HelpCenter />} />
                      <Route
                        path="/join/:campaign"
                        element={<CampaignLanding />}
                      />
                      <Route path="/download-app" element={<DownloadApp />} />
                      <Route
                        path="/reklaim-invest"
                        element={<ReklaimInvest />}
                      />
                      <Route path="/optout" element={<Optout />} />
                      <Route path="/geo-blocked" element={<GeoBlocked />} />

                      {/* Legacy redirects (inside marketing layout) */}
                      <Route
                        path="/signals"
                        element={<MarketingNextRedirect path="/how-it-works" />}
                      />
                      <Route
                        path="/rewards"
                        element={
                          <MarketingNextRedirect path="/how-it-works#earn" />
                        }
                      />
                      <Route
                        path="/home"
                        element={<MarketingNextRedirect path="/" />}
                      />
                      <Route
                        path="/vip-program"
                        element={<MarketingNextRedirect path="/how-it-works" />}
                      />
                      <Route
                        path="/learn/category/*"
                        element={<Navigate to="/learn" replace />}
                      />
                      <Route
                        path="/learn/tag/*"
                        element={<Navigate to="/learn" replace />}
                      />
                      <Route
                        path="/partner-blog/category/*"
                        element={<Navigate to="/partner-blog" replace />}
                      />
                      <Route
                        path="/partner-blog/tag/*"
                        element={<Navigate to="/partner-blog" replace />}
                      />
                      <Route
                        path="/webinars/tag/*"
                        element={<Navigate to="/webinars" replace />}
                      />
                      <Route
                        path="/investors/press/tag/*"
                        element={<Navigate to="/investors" replace />}
                      />
                      <Route
                        path="/careers/current-opportunities"
                        element={<Navigate to="/careers" replace />}
                      />
                    </Route>

                    {/* Legal pages -- self-contained layout (LegalPage shell + per-page data files in `src/pages/_data/legal/`) */}
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/cookies" element={<CookiePolicy />} />
                    <Route path="/do-not-sell" element={<DoNotSellMyData />} />
                    <Route
                      path="/notice-of-incentive"
                      element={<NoticeOfIncentive />}
                    />
                    <Route
                      path="/delete-my-account"
                      element={<DeleteMyAccount />}
                    />
                    <Route
                      path="/data-broker-privacy-notice"
                      element={<DataBrokerPrivacyNotice />}
                    />

                    {/* Auth & onboarding routes -- self-contained layouts (each
                     * page renders its own header). Intentionally NOT wrapped in
                     * `MarketingLayout`; that is the contract documented in
                     * `docs/context/FEATURE_SPECS/public-marketing-routes-and-auth.md`
                     */}
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/confirm" element={<AuthConfirm />} />
                    <Route path="/auth/one-tap" element={<AuthOneTap />} />
                    <Route path="/auth/signup" element={<AuthSignup />} />
                    <Route
                      path="/auth/sso-callback"
                      element={<AuthSsoCallback />}
                    />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/onboarding" element={<Onboarding />} />

                    {/* Survey deep link (REK-1924) — public shareable
                     * `/s/:id` resolver; redirects into the dashboard surveys
                     * section (via /auth when logged out). */}
                    <Route path="/s/:id" element={<SurveyDeepLink />} />

                    {/* Dashboard */}
                    <Route path="/dashboard/*" element={<Dashboard />} />

                    {/* Admin — password-reset pages deleted in REK-2166 PR2; stale
                        URLs redirect to Clerk OTP login (D-4). */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route
                      path="/admin/forgot-password"
                      element={<Navigate to="/admin/login" replace />}
                    />
                    <Route
                      path="/admin/reset-password"
                      element={<Navigate to="/admin/login" replace />}
                    />
                    <Route path="/admin/*" element={<AdminDashboard />} />

                    {/* Utility routes */}
                    <Route
                      path="/docusign/callback"
                      element={<DocuSignCallback />}
                    />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <CookieConsentBanner />
              </ErrorBoundary>
            </ProfileWizardProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
